import React, { useMemo } from "react";
import { useGLTF } from "@react-three/drei";
import { Box3, Euler, TextureLoader, Vector3 } from "three";
import { PrimitiveProps, useLoader } from "@react-three/fiber";
import { Landmark } from "@mediapipe/tasks-vision";
import { scaleLandmark } from "./utils";

interface EarringMeshProps extends Omit<PrimitiveProps, "object"> {
  model?: string;
  image?: string;
  landmarks: Landmark[];
  videoWidth: number,
  videoHeight: number,
}

const LANDMARKS = {
  LEFT_MOST: 132,
  RIGHT_MOST: 361,
  SECOND_LEFT_MOST: 177,
  SECOND_RIGHT_MOST: 401,
  THIRD_LEFT_MOST: 147,
  THIRD_RIGHT_MOST: 376,
};

const calculateDifference = (landmark1: Landmark, landmark2: Landmark) => ({
  ...landmark1,
  x: landmark1.x - landmark2.x,
});

const calculateSum = (landmark1: Landmark, landmark2: Landmark) => ({
  ...landmark1,
  x: landmark1.x + landmark2.x,
});

const computeEarringTransform = (
  landmarks: Landmark[],
  isLeft: boolean,
  yOffsetCalc: number,
  videoWidth: number,
  videoHeight: number,
):{
  scale: Vector3
  position: Vector3
  rotation: Euler
} => {
  const leftEye = scaleLandmark(landmarks[LANDMARKS.LEFT_MOST], videoWidth, videoHeight);
  const rightEye = scaleLandmark(landmarks[LANDMARKS.RIGHT_MOST], videoWidth, videoHeight);
  const eyeDist = Math.sqrt(
    (leftEye.x - rightEye.x) ** 2 +
    (leftEye.y - rightEye.y) ** 2 +
    (leftEye.z - rightEye.z) ** 2
  );
  const computedScale = eyeDist / 50;

  const yOffset = computedScale * yOffsetCalc;
  const scale = new Vector3(computedScale, computedScale, computedScale);

  const side = isLeft ? "LEFT" : "RIGHT";
  const mostLandmark = scaleLandmark(landmarks[LANDMARKS[`${side}_MOST`]], videoWidth, videoHeight);
  const secondMostLandmark = scaleLandmark(
    landmarks[LANDMARKS[`SECOND_${side}_MOST`]],
    videoWidth,
    videoHeight
  );
  const difference = calculateDifference(mostLandmark, secondMostLandmark);
  const earlobeLandmark = calculateSum(mostLandmark, difference);

  const zLandmark = scaleLandmark(
    landmarks[LANDMARKS[`THIRD_${side}_MOST`]],
    videoWidth,
    videoHeight
  );

  const position = new Vector3(earlobeLandmark.x, earlobeLandmark.y - yOffset, zLandmark.z);
  const rotation = new Euler(0, 0, 0);

  return { scale, position, rotation };
};

const EarringMesh = ({ model, image, landmarks, ...props }: EarringMeshProps) => {

  return (
    <>
    { model && <Render3DModel model={model} landmarks={landmarks} {...props}/> }
    { image && <Render2DImage image={image} landmarks={landmarks} {...props}/> }
    </>
  );
};

const Render3DModel = ({ model, landmarks,videoWidth, videoHeight, ...props }: Omit<EarringMeshProps, 'model' | 'image' |  'videoWidth'|'videoHeight' > & { model: string } ) => {
  const gltfModel = useGLTF(model);
  const scene = Array.isArray(gltfModel) ? gltfModel[0].scene : gltfModel.scene;

  const bbox = new Box3().setFromObject(scene);
  const size = bbox.getSize(new Vector3());
  const leftYOffset = size.y
  const rightYOffset = size.y + 1.25

  const leftEarringTransform = useMemo(
    () => computeEarringTransform(landmarks, true, leftYOffset,videoWidth, videoHeight),
    [landmarks, leftYOffset, videoHeight, videoWidth]
  );
  const rightEarringTransform = useMemo(
    () => computeEarringTransform(landmarks, false, rightYOffset,videoWidth, videoHeight),
    [landmarks, rightYOffset, videoHeight, videoWidth]
  );

  return (
    <>
      {[leftEarringTransform, rightEarringTransform].map((transform, index) => (
        <primitive
          key={index}
          object={scene.clone()}
          scale={transform.scale}
          position={transform.position}
          rotation={transform.rotation}
          {...props}
        />
      ))}
    </>
  );
}

const Render2DImage = ({ image, landmarks,videoWidth, videoHeight }: Pick<EarringMeshProps, 'landmarks'|  'videoWidth'|'videoHeight'> & { image: string }) => {
  const texture = useLoader(TextureLoader, image);

  const size = new Vector3(3, 9, 1)

  const leftYOffset = 1 / 2 * (size.y - 3)
  const rightYOffset = 1 / 2 * (size.y + 1)

  const leftEarringTransform = useMemo(
    () => computeEarringTransform(landmarks, true, leftYOffset,videoWidth, videoHeight),
    [landmarks, leftYOffset, videoHeight, videoWidth]
  );
  const rightEarringTransform = useMemo(
    () => computeEarringTransform(landmarks, false, rightYOffset,videoWidth, videoHeight),
    [landmarks, rightYOffset, videoHeight, videoWidth]
  );

  return (
    <>
      {[leftEarringTransform, rightEarringTransform].map((transform, index) => (
        <mesh
          key={index}
          scale={[transform.scale.x * size.x, transform.scale.y * size.y, size.z]}
          position={transform.position}
          rotation={transform.rotation}
        >
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial map={texture} transparent />
        </mesh>
      ))}
    </>
  );
}


export default EarringMesh;
